import { OrganizationStatus } from '@processpuzzle/platform-admin';

/**
 * The five locales ProcessPuzzle ships, which is narrower than the free-text BCP-47 tag Admin
 * accepts.
 *
 * The narrowing is the public gateway earning its place, and it is mirrored here rather than
 * inferred from the language config: provisioning a tenant in a locale the application has no
 * translations for produces a working tenant nobody can read.
 */
export enum SupportedLocale {
  DE = 'de',
  EN = 'en',
  ES = 'es',
  FR = 'fr',
  HU = 'hu',
}

/** Whether the invoiced party is a company or a private individual. `CONSUMER` is refused in v1. */
export enum CustomerType {
  BUSINESS = 'BUSINESS',
  CONSUMER = 'CONSUMER',
}

/**
 * A postal address. `country` is its own field, not the last line of a free-text block, because it
 * selects the VAT treatment: German business, EU business with a valid VAT ID (reverse charge), EU
 * consumer, or outside the EU.
 */
export interface PostalAddress {
  street: string;
  postalCode: string;
  city: string;
  /** ISO-3166-1 alpha-2, upper case. */
  country: string;
}

/**
 * Who is invoiced, as distinct from who administers the application.
 *
 * Collected for a compliant invoice under § 14 UStG rather than for provisioning: none of it is
 * needed to bring a tenant up, and all of it is needed before an invoice can be issued. That is also
 * why `billingEmail` is separate from the administrator's address — an accounting mailbox outlives
 * any individual, and whoever signs up is usually not whoever pays.
 *
 * `vatIdValidatedAt` is deliberately absent from this model as it is from the contract: the VIES
 * confirmation is stamped server-side, and a client-supplied validation timestamp would be evidence
 * of nothing. Changing `vatId` clears it on the server, which is why the portal's form warns before
 * saving a new one.
 */
export interface BillingParty {
  legalName: string;
  legalForm: string;
  customerType: CustomerType;
  registeredAddress: PostalAddress;
  /** Where invoices are addressed when that differs. Absent means the registered address. */
  billingAddress?: PostalAddress;
  vatId?: string;
  registrationNumber?: string;
  billingEmail: string;
}

/** Identity of the tenant being created. The key is permanent once claimed. */
export interface SignupOrganization {
  key: string;
  name: string;
  defaultLocale: SupportedLocale;
}

/**
 * The tenant's first administrator. No username — the email is the login name, so the form asks
 * once. No password either: the user is created with a required password reset and sets their own
 * from the invitation.
 */
export interface SignupAdministrator {
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  defaultLocale?: SupportedLocale;
}

/** What is being bought. No payment method: the trial takes no card. */
export interface SignupSubscription {
  planCode: string;
  quantity: number;
  /**
   * The Business Starter the application is seeded from, by catalog id. Absent means an empty
   * application — a choice in its own right, not a step left unfinished, which is why
   * {@link isDraftComplete} does not ask for it.
   */
  starterId?: string;
}

/**
 * Which *texts* were agreed to.
 *
 * Versions rather than booleans, and that is the contract's design rather than this form's: "they
 * agreed" only means something as "they agreed to this version", and the version is the evidence a
 * boolean would merely duplicate. The Terms of Service and the DPA are required checkboxes, so a
 * submitted form is their acceptance and a `false` would simply be an invalid request.
 *
 * `marketingOptIn` is the one genuine boolean, and it must not be pre-ticked: § 7 UWG requires
 * consent for B2B advertising email too, so bundling it with the required two would invalidate it.
 */
export interface SignupConsent {
  termsOfServiceVersion: string;
  dataProcessingAgreementVersion: string;
  marketingOptIn: boolean;
}

/** The subscription form, submitted whole. */
export interface SignupRequest {
  organization: SignupOrganization;
  billingParty: BillingParty;
  administrator: SignupAdministrator;
  subscription: SignupSubscription;
  consent: SignupConsent;
}

/**
 * How far bringing the customer's application up has got.
 *
 * Mirrors the gateway's `SeedState` and, through it, the seed job the design in
 * `docs/customer-application-seed.md` §4 describes. The confirmation page watches this rather than
 * `status`, which is `PROVISIONING` for the whole of an onboarding and therefore cannot tell a page
 * that is still working from one that has stopped.
 */
export enum SeedState {
  PENDING = 'PENDING',
  PROVISIONING_IDENTITY = 'PROVISIONING_IDENTITY',
  PROVISIONING_DATA = 'PROVISIONING_DATA',
  READY = 'READY',
  FAILED = 'FAILED',
}

/**
 * How the administrator was provisioned. `EXISTING` means the e-mail already had an account, which
 * was invited to the new organization instead of being sent an activation link.
 */
export enum AdministratorAccount {
  NEW = 'NEW',
  EXISTING = 'EXISTING',
}

/**
 * State of a submitted sign-up returned by the status endpoint.
 *
 * `applicationUrl` is present only once `status` is `ACTIVE`: the tenant's identity does not exist
 * while provisioning, so a link offered early would fail login. The confirmation page does not
 * render it even then — the activation e-mail is the way in — but the customer portal does, and
 * both read this model.
 *
 * `seedState` is absent for any organization that has no seed job, which is every tenant not
 * created through this funnel. A page that reaches this for such a key falls back to `status`.
 */
export interface Signup {
  orgKey: string;
  status: OrganizationStatus;
  applicationUrl?: string;
  trialEndsAt?: string;
  seedState?: SeedState;
  /** Which step failed. Present only when `seedState` is `FAILED`. */
  failedStep?: SeedState;
  /**
   * The server's one-sentence reason, present only when `seedState` is `FAILED`.
   *
   * Carried and deliberately not rendered. It arrives as a single English sentence, and this funnel
   * ships in five languages — so the page renders its own translated message keyed off
   * `failedStep` instead. What this is for is the support conversation: it is in the response a
   * customer can be asked to screenshot, and it is what the `seed_jobs` row will corroborate.
   */
  seedError?: string;
  /** Absent until the identity step has decided; see {@link AdministratorAccount}. */
  administratorAccount?: AdministratorAccount;
}

/** Whether provisioning for a sign-up remains in progress. */
export function isProvisioning(signup: Signup | undefined): boolean {
  return signup?.status === OrganizationStatus.PROVISIONING;
}

/**
 * Whether the seed has stopped moving, either way.
 *
 * What the confirmation page's poll tests, so that it stops on a failure rather than only on
 * success — a poll that ran until it succeeded would run for ever against a job nobody is coming
 * to fix.
 *
 * A sign-up with no seed state at all is terminal here too: there is no job, so nothing will ever
 * advance, and polling for one would be waiting on something that does not exist.
 */
export function isSeedSettled(signup: Signup | undefined): boolean {
  if (!signup) return false;
  return signup.seedState === undefined || signup.seedState === SeedState.READY || signup.seedState === SeedState.FAILED;
}

/**
 * The five steps of the funnel, in order.
 *
 * An array of literals rather than an enum, because the stepper needs the order and the store needs
 * to index into it — and the order *is* the meaning here: the plan is chosen before the key, and the
 * consent is last because it names the documents shown alongside everything above it.
 */
export const SIGNUP_STEPS = ['plan', 'organization', 'billingParty', 'administrator', 'consent'] as const;

export type SignupStep = (typeof SIGNUP_STEPS)[number];

/**
 * The form as it is being filled in, with every part optional.
 *
 * Separate from {@link SignupRequest} rather than a `Partial` of it, because it is what survives
 * `sessionStorage` across the Keycloak round trip and what each step reads and writes. A `Partial`
 * would make the whole nested tree optional at every level, which is exactly what the steps need —
 * but it would also let a half-filled billing party be passed to the service as though it were
 * complete. {@link isDraftComplete} is the one place the two are bridged.
 */
export interface SignupDraft {
  organization?: Partial<SignupOrganization>;
  billingParty?: Partial<BillingParty>;
  administrator?: Partial<SignupAdministrator>;
  subscription?: Partial<SignupSubscription>;
  consent?: Partial<SignupConsent>;
}

/**
 * Whether a draft carries everything the contract requires, and can therefore be submitted.
 *
 * This duplicates no validation: the per-step forms own the field-level rules, and this is the
 * question "have all five steps been completed at all", which no single step can answer. It exists
 * because a draft restored from storage may be from an older visit and may be missing a step that
 * has since been added to the funnel — a case the forms cannot see, since they were never rendered.
 */
export function isDraftComplete(draft: SignupDraft): draft is Required<SignupDraft> {
  return (
    !!draft.subscription?.planCode &&
    !!draft.subscription?.quantity &&
    !!draft.organization?.key &&
    !!draft.organization?.name &&
    !!draft.organization?.defaultLocale &&
    !!draft.billingParty?.legalName &&
    !!draft.billingParty?.legalForm &&
    !!draft.billingParty?.customerType &&
    !!draft.billingParty?.registeredAddress?.country &&
    !!draft.billingParty?.billingEmail &&
    !!draft.administrator?.email &&
    !!draft.administrator?.firstName &&
    !!draft.administrator?.lastName &&
    !!draft.consent?.termsOfServiceVersion &&
    !!draft.consent?.dataProcessingAgreementVersion
  );
}
